// src/features/sessions/lib/artifactPermissions.ts
//
// Single source of truth for "can this user author/edit a session note or case
// report?" — mirrored 1:1 from the elixaa-core service guards so the FE never
// shows an Edit / Create / Start-blank affordance the backend will 403.
//
// Backend references (elixaa-api/src/services):
//   • sessionNoteService.updateNote  216-219  — note edit
//     createNote 66-70, submitNote 275-277   — note create / submit (same rule)
//   • caseReportService.createReport 267-270  — case report create (same rule as note)
//   • caseReportService.updateReport 614-616  — case report edit
//     submitReport 676-678                    — case report submit (same rule as edit)
//
// Identity note: Practitioner.id === User.id in this system (see
// src/lib/rbac/useClinicalRole.ts), so session.practitionerId and
// report.authorId compare directly against the logged-in user's id.

import type { RoleType } from '@/lib/rbac';

/** Minimal shape read off the auth store — accepts `useAuthStore(s => s.user)`. */
export interface ArtifactUser {
  id: string;
  role: RoleType | null;
}

/**
 * Roles allowed to author a note / create a case report on ANY session,
 * regardless of who handled it. Matches the backend's `isAdminOrChief*`
 * escape hatch plus the platform super admin.
 */
function isElevatedAuthor(role: RoleType | null): boolean {
  return role === 'super_admin' || role === 'ba_super_admin' || role === 'chief_psychologist';
}

/**
 * May the user create / edit / submit the SESSION NOTE for this session?
 * BE: the session's own practitioner, or a super_admin / ba_super_admin /
 * chief_psychologist.
 */
export function canAuthorSessionNote(
  user: ArtifactUser | null | undefined,
  sessionPractitionerId: string | null | undefined,
): boolean {
  if (!user) return false;
  if (sessionPractitionerId != null && user.id === sessionPractitionerId) return true;
  return isElevatedAuthor(user.role);
}

/**
 * May the user CREATE a case report for this session? Identical rule to
 * {@link canAuthorSessionNote} on the backend (caseReportService.createReport).
 * Kept as its own export so call sites read intently and the two can diverge
 * if the backend ever does.
 */
export function canCreateCaseReport(
  user: ArtifactUser | null | undefined,
  sessionPractitionerId: string | null | undefined,
): boolean {
  return canAuthorSessionNote(user, sessionPractitionerId);
}

/**
 * May the user EDIT / SUBMIT this specific case report? BE is stricter than
 * create: only the report's author, or a platform super_admin. A chief or
 * ba_super_admin who did NOT author the report is rejected server-side, so
 * we hide Edit from them here too.
 */
export function canEditCaseReport(
  user: ArtifactUser | null | undefined,
  reportAuthorId: string | null | undefined,
): boolean {
  if (!user) return false;
  if (reportAuthorId != null && user.id === reportAuthorId) return true;
  return user.role === 'super_admin';
}
